// improvements.js — v1.0.0 Phase 3 (Lease-Leverage Module)
//
// Thin data-access layer for the improvements API. The server-side
// get-improvements function reads from improvements_with_current_value
// (a Postgres view with security_invoker = true) so each returned row
// already carries a freshly-computed straight-line current_value. The
// create/update endpoints also re-read from the view so callers always
// get the latest book value back without a second fetch.
//
// {data, error} shape matches the rest of the codebase.
import { supabase } from './supabaseClient';
import { API_BASE_URL } from '../services/api';

const NOT_SIGNED_IN = {
  data: null,
  error: { message: 'Not signed in. Please sign in and try again.' },
};
const NOT_CONFIGURED = {
  data: null,
  error: {
    message:
      'Supabase is not configured. Fill in frontend/.env (REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY) and restart the dev server.',
  },
};

async function callApi(path, init = {}) {
  if (!supabase) return NOT_CONFIGURED;
  const { data: sessionData } = await supabase.auth.getSession();
  const accessToken = sessionData?.session?.access_token;
  if (!accessToken) return NOT_SIGNED_IN;
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
        ...(init.headers || {}),
      },
    });
    const payload = await response.json().catch(() => null);
    if (!response.ok || payload?.success === false) {
      return {
        data: null,
        error: {
          message: payload?.error || `Request failed (HTTP ${response.status}).`,
          status: response.status,
          payload,
        },
      };
    }
    return { data: payload, error: null };
  } catch (err) {
    return { data: null, error: { message: err?.message || 'Network error.' } };
  }
}

/**
 * Create a capital improvement on a lead.
 * Required fields: lead_id, asset_id, description, category, cost, useful_life_years.
 * 409 on duplicate (lead_id, asset_id).
 * Returns { data: { improvement }, error } — improvement carries current_value.
 */
export async function createImprovement(input) {
  return callApi('/create-improvement', {
    method: 'POST',
    body: JSON.stringify(input || {}),
  });
}

/**
 * List all improvements on a lead, ordered by asset_id ASC.
 * Each row carries current_value (computed straight-line book value at read time).
 * Returns { data: { improvements: [...] }, error }.
 */
export async function getImprovements(leadId) {
  return callApi(`/get-improvements?lead_id=${encodeURIComponent(leadId)}`, {
    method: 'GET',
  });
}

/**
 * PATCH a partial set of fields. asset_id is patchable — UNIQUE collision
 * surfaces as 409. Returns { data: { improvement }, error } with fresh
 * current_value re-read from the view.
 */
export async function updateImprovement(patch) {
  return callApi('/update-improvement', {
    method: 'PATCH',
    body: JSON.stringify(patch || {}),
  });
}

/**
 * Delete an improvement by id. Returns { data: { success: true }, error }.
 * 404 on cross-user.
 */
export async function deleteImprovement(id) {
  return callApi('/delete-improvement', {
    method: 'DELETE',
    body: JSON.stringify({ id }),
  });
}
